// Theme configurations

import { ThemeConfig, ColorPalette, TypographyConfig, LayoutConfig } from '../types/theme';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('themes');

// Base typography configuration (shared across themes)
const baseTypography: TypographyConfig = {
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  headingFontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  monospaceFontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, "Courier New", monospace',
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    xxl: '24px',
  },
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// Base layout configuration (shared across themes)
const baseLayout: LayoutConfig = {
  sidebarWidth: 60,
  headerHeight: 64,
  borderRadius: {
    sm: '4px',
    base: '6px',
    lg: '8px',
    xl: '12px',
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    base: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    lg: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
};

// Default theme (current Hop Studio styling)
const defaultColors: ColorPalette = {
  primary: '#1890ff',
  primaryHover: '#40a9ff',
  primaryActive: '#096dd9',
  secondary: '#52c41a',
  secondaryHover: '#73d13d',
  secondaryActive: '#389e0d',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  info: '#1890ff',
  
  background: '#ffffff',
  backgroundSecondary: '#fafafa',
  surface: '#ffffff',
  surfaceSecondary: '#f5f5f5',
  
  textPrimary: '#262626',
  textSecondary: '#595959',
  textDisabled: '#bfbfbf',
  textInverse: '#ffffff',
  
  border: '#d9d9d9',
  borderSecondary: '#f0f0f0',
  borderHover: '#40a9ff',
  
  sidebarBackground: '#001529',
  sidebarText: 'rgba(255, 255, 255, 0.65)',
  sidebarTextHover: 'rgba(255, 255, 255, 0.85)',
  sidebarTextActive: '#ffffff',
  
  headerBackground: '#001529',
  headerText: '#ffffff',
  headerBorder: '#001529',
};

export const defaultTheme: ThemeConfig = {
  id: 'default',
  name: 'Default (Hop Studio)',
  description: 'The original Hop Studio theme with blue accents',
  branding: {
    appName: 'Hop Studio',
    logoUrl: '/src/assets/putki-logo.svg',
    faviconUrl: '/favicon.ico',
    companyName: 'Apache Hop',
    companyUrl: 'https://hop.apache.org',
  },
  colors: defaultColors,
  typography: baseTypography,
  layout: baseLayout,
  isDark: false,
};

// Note: Dark mode is now handled via Ant Design's darkAlgorithm
// The algorithm automatically generates dark variants from seed tokens

// Know.bi theme (based on their branding)
const knowbiColors: ColorPalette = {
  primary: '#6366f1', // Indigo-500 (know.bi primary color)
  primaryHover: '#8b5cf6', // Purple-500
  primaryActive: '#4f46e5', // Indigo-600
  secondary: '#10b981', // Emerald-500
  secondaryHover: '#34d399', // Emerald-400
  secondaryActive: '#059669', // Emerald-600
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#6366f1',
  
  background: '#ffffff',
  backgroundSecondary: '#f8fafc',
  surface: '#ffffff',
  surfaceSecondary: '#f1f5f9',
  
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  textDisabled: '#cbd5e1',
  textInverse: '#ffffff',
  
  border: '#e2e8f0',
  borderSecondary: '#f1f5f9',
  borderHover: '#8b5cf6',
  
  sidebarBackground: '#1e293b', // Slate-800
  sidebarText: 'rgba(255, 255, 255, 0.7)',
  sidebarTextHover: 'rgba(255, 255, 255, 0.9)',
  sidebarTextActive: '#ffffff',
  
  headerBackground: '#1e293b',
  headerText: '#ffffff',
  headerBorder: '#334155',
};

export const knowbiTheme: ThemeConfig = {
  id: 'knowbi',
  name: 'Know.bi',
  description: 'Professional theme inspired by know.bi branding',
  branding: {
    appName: 'Data Studio',
    logoUrl: 'https://www.know.bi/web/image/website/1/logo/know.bi?unique=72057dc',
    logoUrlDark: 'https://www.know.bi/web/image/website/1/logo/know.bi?unique=72057dc',
    faviconUrl: '/favicon.ico',
    companyName: 'Know.bi',
    companyUrl: 'https://www.know.bi',
  },
  colors: knowbiColors,
  typography: {
    ...baseTypography,
    fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    headingFontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  layout: baseLayout,
  isDark: false,
};

// Export all available themes (dark mode is handled separately via algorithm)
export const availableThemes: ThemeConfig[] = [
  defaultTheme,
  knowbiTheme,
];

// Helper function to get theme by ID
export const getThemeById = (themeId: string): ThemeConfig => {
  const theme = availableThemes.find(t => t.id === themeId);
  if (!theme) {
    logger.warn(`Theme with ID "${themeId}" not found, falling back to default theme`);
    return defaultTheme;
  }
  return theme;
};

// Helper function to get default theme ID from environment
export const getDefaultThemeId = (): string => {
  return import.meta.env.VITE_DEFAULT_THEME || 'default';
};
